import { useEffect, useState } from 'react';

import { COVER_PHOTO, STOCK_CLIP, TEXT_OVERLAY } from '@/constants';
import { AssetType } from '@/enums';
import { isVideoAsset } from '@/helpers';
import CropAndFilter from '@/pages/Event/Asset/AssetCropAndFilter';
import { AudioPlayer } from '@/pages/Event/Asset/AudioPlayer';
import { VideoAssetEdit } from '@/pages/Event/Asset/VideoAssetEdit';
import { TextDetails } from '@/pages/Event/Text/Details';
import { EditSongPopup } from '@/pages/Event/components/EditSongPopup';
import { StockClipDetails } from '@/pages/StockLibrary/StockClipDetails';
import { useGetCoverPhotoQuery } from '@/services';
import { Asset, AssetDetailsProps } from '@/types';

export const AssetDetails = ({
  asset,
  setAssetDetailsLabel,
  enableEdit,
  setEnableEdit,
  allEventAssets,
  closeModal
}: AssetDetailsProps) => {
  useEffect(() => {
    if (asset?.assetType?.toUpperCase() === AssetType[AssetType.TEXT]) {
      setAssetDetailsLabel?.('textDetails');
    }

    return () => {
      setAssetDetailsLabel?.('');
    };
  }, [asset]);

  const { data: coverPhoto } = useGetCoverPhotoQuery(
    asset?.assetType === COVER_PHOTO ? Number(asset?.eventId) : null,
    asset?.assetType === COVER_PHOTO ? Number(asset?.id) : null
  );

  const [isStockClipModalOpen, setIsStockClipModalOpen] = useState<boolean>(false);

  const handleClose = () => {
    setIsStockClipModalOpen(false);
    closeModal?.();
  };

  useEffect(() => {
    setIsStockClipModalOpen(asset?.assetType?.toUpperCase() === STOCK_CLIP.toUpperCase());
  }, [asset]);

  const getAssetContent = () => {
    let assetContent = null;

    const tributevideo = AssetType[AssetType.TRIBUTE_VIDEO].replace(/_/g, '');

    switch (asset?.assetType?.toUpperCase()) {
      case TEXT_OVERLAY.toUpperCase():
      case AssetType[AssetType.IMAGE]:
        assetContent =
          enableEdit && setEnableEdit ? (
            <CropAndFilter
              asset={asset}
              allEventAssets={allEventAssets as Asset[]}
              setEnableEdit={setEnableEdit}
              closeModal={closeModal as () => void}
            />
          ) : (
            <img src={asset?.assetUrl} className='border-2 object-contain' />
          );
        break;
      case COVER_PHOTO.toUpperCase():
        if (isVideoAsset(asset) || isVideoAsset(coverPhoto?.data?.backgroundAsset)) {
          assetContent = (
            <video
              src={coverPhoto?.data?.finalAsset?.assetUrl ?? asset?.assetUrl}
              className='h-[400px]'
              autoPlay
              muted
              loop
            />
          );
        } else {
          assetContent = <img src={asset?.assetUrl} className='object-contain' />;
        }
        break;
      case STOCK_CLIP.toUpperCase():
        assetContent = isStockClipModalOpen && (
          <StockClipDetails asset={asset} isModalOpen={true} onClose={handleClose} />
        );
        break;
      case AssetType[AssetType.VIDEO]:
      case tributevideo:
        assetContent =
          enableEdit && setEnableEdit ? (
            <VideoAssetEdit
              asset={asset}
              closeModal={closeModal as () => void}
              eventId={asset.eventId}
            />
          ) : (
            <video controls src={asset?.assetUrl} className='h-[400px]' />
          );
        break;
      case AssetType[AssetType.AUDIO]:
        assetContent =
          enableEdit && setEnableEdit ? (
            <EditSongPopup
              songUrl={asset.assetUrl}
              originalAssetName={asset.assetName}
              closeModal={closeModal}
              eventId={asset.eventId}
            />
          ) : (
            <AudioPlayer audioUrl={asset?.assetUrl} />
          );
        break;
      case AssetType[AssetType.TEXT]:
        assetContent = <TextDetails asset={asset} />;
        break;
      default:
        assetContent = null;
        break;
    }
    return assetContent;
  };

  return <div className='flex size-full justify-center'>{getAssetContent()}</div>;
};
